import type { ReactNode } from "react";
import { cn } from "./utils/cn";
import SlidingActiveIndicator from "./SlidingActiveIndicator";

export interface TabItem<T extends string = string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface TabsProps<T extends string = string> {
  value: T;
  onChange: (value: T) => void;
  items: TabItem<T>[];
  /** `segmented` = pill control (default); `underline` = tab bar with bottom border. */
  variant?: "segmented" | "underline";
  size?: "sm" | "md";
  fullWidth?: boolean;
  className?: string;
}

/**
 * Canonical tab control. One look for every set of tabs in the app — replaces
 * MUI `<Tabs>` and the various hand-rolled segmented controls.
 */
function Tabs<T extends string = string>({
  value,
  onChange,
  items,
  variant = "segmented",
  size = "md",
  fullWidth,
  className,
}: TabsProps<T>) {
  if (variant === "underline") {
    return (
      <div
        role="tablist"
        className={cn(
          "relative flex border-b border-border-subtle",
          size === "sm" ? "gap-4" : "gap-1",
          fullWidth && "w-full",
          className
        )}
      >
        {items.map((it) => {
          const active = it.value === value;
          return (
            <button
              key={it.value}
              type="button"
              role="tab"
              data-slide-id={it.value}
              data-slide-disabled={it.disabled ? "true" : undefined}
              aria-selected={active}
              disabled={it.disabled}
              onClick={() => onChange(it.value)}
              className={cn(
                "transition-colors relative z-[1] -mb-px inline-flex items-center gap-2 border-b-2 border-transparent font-medium duration-200 focus-visible:outline-none",
                size === "sm" ? "px-1 py-1.5 text-sm" : "px-4 py-2.5 text-sm",
                fullWidth && "flex-1 justify-center",
                active
                  ? "text-secondary"
                  : "text-menu/55 hover:text-menu dark:text-white dark:hover:text-white",
                it.disabled && "cursor-not-allowed opacity-50"
              )}
            >
              {it.icon}
              {it.label}
            </button>
          );
        })}
        <SlidingActiveIndicator activeId={value} axis="x">
          <span className="absolute inset-x-0 bottom-0 h-0.5 bg-secondary" />
        </SlidingActiveIndicator>
      </div>
    );
  }

  return (
    <div
      role="tablist"
      className={cn(
        "relative inline-flex items-center gap-1 rounded-pill bg-surface-subtle p-1",
        fullWidth && "flex w-full",
        className
      )}
    >
      {items.map((it) => {
        const active = it.value === value;
        return (
          <button
            key={it.value}
            type="button"
            role="tab"
            data-slide-id={it.value}
            data-slide-disabled={it.disabled ? "true" : undefined}
            aria-selected={active}
            disabled={it.disabled}
            onClick={() => onChange(it.value)}
            className={cn(
              "transition-colors relative z-[1] inline-flex items-center justify-center gap-2 rounded-pill px-4 text-sm font-medium focus-visible:outline-none focus-visible:ring focus-visible:ring-offset-0",
              size === "sm" ? "h-control-sm" : "h-control",
              fullWidth && "flex-1",
              active ? "text-secondary" : "text-menu/70 hover:text-menu",
              it.disabled && "cursor-not-allowed opacity-50"
            )}
          >
            {it.icon}
            {it.label}
          </button>
        );
      })}
      <SlidingActiveIndicator
        activeId={value}
        axis="x"
        className="rounded-pill bg-surface shadow-e1"
      />
    </div>
  );
}

export default Tabs;
